import React, { useState, useEffect, useRef } from "react";
import ClearTickersModal from "../components/ClearTickersModal";

// TickerInput - a single input field for a stock and its weight
// index: the index of the ticker in the array
// stock: the current stock value
// weight: the current weight value
// handleStockChange: function to handle changes in the stock input
// handleWeightChange: function to handle changes in the weight input
// disabled: boolean to disable the weight input
const TickerInput = ({
	index,
	stock,
	minWeight,
	maxWeight,
	handleStockChange,
	handleMinWeightChange,
	handleMaxWeightChange,
	disabled,
}) => {
	return (
		<div className="flex gap-2 w-full relative">
			{/* input field for stocks */}
			<input
				type="text"
				value={stock}
				onChange={(e) => handleStockChange(index, e.target.value)}
				placeholder="Stock"
				className="border p-2 rounded w-full peer"
			/>
			{/* input fields for weights */}
			<input
				type="number"
				value={minWeight}
				onChange={(e) => handleMinWeightChange(index, e.target.value)}
				placeholder="Min Weight"
				disabled={disabled} //handles our disabled state
				className={`border p-2 rounded w-full transition-all ${
					disabled
						? "bg-gray-200 cursor-not-allowed text-gray-400"
						: "text-gray-500"
				}`}
			/>
			<input
				type="number"
				value={maxWeight}
				onChange={(e) => handleMaxWeightChange(index, e.target.value)}
				placeholder="Max Weight"
				disabled={disabled} //handles our disabled state
				className={`border p-2 rounded w-full transition-all ${
					disabled
						? "bg-gray-200 cursor-not-allowed text-gray-400"
						: "text-gray-500"
				}`}
			/>
		</div>
	);
};

//Tickers component manages the list of tickers and their weights.
const Tickers = ({ generateGraph }) => {
	//state to hold a list of each ticker, with stock and weight
	//state to track if weights is disabled
	const [tickers, setTickers] = useState([
		{ symbol: "", min_weight: 0, max_weight: 1 },
	]);
	const [weightsDisabled, setWeightsDisabled] = useState(false);
	const [addedTicker, setAddedTicker] = useState(false);

	useEffect(() => {
		const savedTickers = localStorage.getItem("tickers");
		if (savedTickers) {
			setTickers(JSON.parse(savedTickers));
		}
	}, []);

	useEffect(() => {
		if (tickers.length > 1)
			localStorage.setItem("tickers", JSON.stringify(tickers));
	}, [tickers]);

	useEffect(() => {
		if (tickerContainerRef.current && addedTicker) {
			const element = document.getElementById("ticker-container");
			if (element) {
				element.scrollTo({
					top: element.scrollHeight,
					behavior: "smooth",
				});
			}
			setAddedTicker(false);
		}
	}, [tickers]);

	//updates the stocks name
	const handleStockChange = (index, value) => {
		const newTickers = [...tickers]; //copy the tickers array
		newTickers[index].symbol = value; //update the stock at the given index
		setTickers(newTickers); //updates the state
	};

	//updates the weight of the stock, same functionality as the handleStockChange
	const handleMinWeightChange = (index, value) => {
		const newTickers = [...tickers];
		newTickers[index].min_weight = value;
		setTickers(newTickers);
	};

	const handleMaxWeightChange = (index, value) => {
		const newTickers = [...tickers];
		newTickers[index].max_weight = value;
		setTickers(newTickers);
	};

	const tickerContainerRef = useRef(null);
	//adds new ticker to list with empty name and weight
	const addTicker = () => {
		setTickers([...tickers, { symbol: "", min_weight: 0, max_weight: 1 }]);
		setAddedTicker(true);
	};

	//removes a ticker from the list at the specified index then updates the state
	const removeTicker = (indexToRemove) => {
		const updatedTickers = tickers.filter((_, i) => i !== indexToRemove);
		setTickers(updatedTickers);
	};

	//toggles thr weightsDisabled state
	const toggleWeights = () => {
		setWeightsDisabled((prevState) => !prevState);
	};

	const disableGenerate = () => {
		return tickers.some((ticker) => ticker.symbol === "");
	};

	const [showModal, setShowModal] = useState(false); // State for modal
	const closeModal = () => {
		setShowModal(false); // Close modal
	};
	const openModal = () => {
		setShowModal(true); // Open modal
	};

	const clearTickers = () => {
		setTickers([{ symbol: "", min_weight: 0, max_weight: 1 }]);
		closeModal();
	};

	return (
		<div className="p-4 w-full h-full lg:w-[30vw]">
			{showModal && (
				<ClearTickersModal
					closeModal={closeModal}
					clearTickers={clearTickers}
				/>
			)}
			<div className="flex mb-4 justify-between pl-2 pr-3">
				<div className="gap-2 flex">
					<button
						onClick={addTicker}
						className="px-4 py-2 rounded text-xs lg:text-sm lg:font-medium transition-all active:scale-95  bg-blue-500 text-white hover:bg-blue-700"
					>
						Add Ticker
					</button>
					<button
						onClick={toggleWeights}
						className={`${
							weightsDisabled
								? "bg-green-600 hover:bg-green-700"
								: "bg-gray-400 hover:bg-gray-700"
						} text-white px-4 py-2 rounded text-xs lg:text-sm lg:font-medium transition-all active:scale-95`}
					>
						{weightsDisabled ? "Enable Weights" : "Disable Weights"}
					</button>
					<button
						onClick={() => generateGraph(tickers)}
						disabled={disableGenerate()}
						className={` text-white px-4 py-2 rounded  text-xs lg:text-sm lg:font-medium transition-all
                        ${
							disableGenerate()
								? "cursor-not-allowed bg-gray-400"
								: "bg-blue-800 hover:bg-blue-900 active:scale-95"
						}`}
					>
						Generate Graph
					</button>
				</div>
				<button
					onClick={openModal}
					className="transtion-all hover:scale-105 active:scale-100 p-1"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="32"
						height="32"
						viewBox="0 0 24 24"
						className="fill-current text-red-500 w-6 h-6"
					>
						<path d="M17 4h5v2h-2v15a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6H2V4h5V2h10zM9 9v8h2V9zm4 0v8h2V9z" />
					</svg>
				</button>
			</div>
			<div
				id="ticker-container"
				className="max-h-[50vh] h-full md:max-h-[calc(100vh-14rem)] px-2 overflow-y-scroll"
				ref={tickerContainerRef}
			>
				{tickers.map((ticker, index) => (
					<div key={index} className="mb-4 flex gap-2 items-center">
						<TickerInput
							index={index}
							stock={ticker.symbol}
							minWeight={ticker.min_weight}
							maxWeight={ticker.max_weight}
							disabled={weightsDisabled}
							handleStockChange={handleStockChange}
							handleMinWeightChange={handleMinWeightChange}
							handleMaxWeightChange={handleMaxWeightChange}
						/>
						<button
							onClick={() => removeTicker(index)}
							className="rounded-lg p-2 hover:bg-gray-200 transition-all active:scale-95"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="32"
								height="32"
								viewBox="0 0 24 24"
								className="fill-current text-[#105790] w-6 h-6"
							>
								<path d="M7 4V2h10v2h5v2h-2v15a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6H2V4zM6 6v14h12V6zm3 3h2v8H9zm4 0h2v8h-2z" />
							</svg>
						</button>
					</div>
				))}
			</div>
		</div>
	);
};

export default Tickers;
